"use client"

import { memo, useEffect, useMemo, useState } from "react"
import { useLocation } from "react-router-dom"
import { motion } from "framer-motion"

import { useMusicContext } from "@/context/musicContext"
import TrackContextMenu from "@/app/pages/playlistPage/components/TrackContextMenu"
import RadioPlayerSidebar from "./components/RadioPlayerSidebar"

import useOrientation from "@/hooks/useOrientation"
import { useRenderTracker } from "@/features/performance/useRenderTracker"
import { HOME_ANIMATION } from "@/app/pages/homePage/utils/home.constants"

import PlayerBackground from "./PlayerBackground"
import PlayerContent from "./PlayerContent"

import { EXTERNAL_ARTISTS } from "@/data/externalArtists"

type Props = {
  variant?: "drawer" | "embedded"
  onClose?: () => void
}

const DEFAULT_BG = "/image/app/logo.jpg"

function PlayerMobil({
  variant = "embedded",
  onClose,
}: Props) {
  const location = useLocation()
  const { isLandscape } = useOrientation()

  const {
    currentSong,
    currentPlaylist,
    currentSongIndex,
    currentTrackData,
    isPlaying,
  } = useMusicContext()

  const [trackMenuOpen, setTrackMenuOpen] = useState(false)
  const [bgImage, setBgImage] = useState(DEFAULT_BG)
  const [bgReady, setBgReady] = useState(true)

  const isRadio = currentTrackData?.type === "radio"
  const isHidden = location.pathname === "/widget"

  /* PLAYLIST */

  const playlistData = useMemo(
    () =>
      Array.isArray(currentPlaylist?.playlistData)
        ? currentPlaylist.playlistData
        : [],
    [currentPlaylist?.playlistData],
  )

  const songsAcontinuacion = useMemo(
    () => playlistData.slice(Math.max(0, currentSongIndex + 1)),
    [playlistData, currentSongIndex],
  )

  /* ARTIST */

  const artistPath =
    currentSong?.artistLink ??
    currentPlaylist?.artistLink ??
    currentPlaylist?.link

  const externalArtist = artistPath
    ? EXTERNAL_ARTISTS[artistPath]
    : null

  /* BACKGROUND */

  const nextBg =
    currentTrackData?.trackImg ||
    currentSong?.songimg ||
    DEFAULT_BG

  useEffect(() => {
    if (nextBg === bgImage) return

    let cancelled = false
    const image = new Image()

    setBgReady(false)

    image.onload = () => {
      if (cancelled) return

      setBgImage(nextBg)

      requestAnimationFrame(() => {
        if (!cancelled) setBgReady(true)
      })
    }

    image.onerror = () => {
      if (cancelled) return

      setBgImage(DEFAULT_BG)
      setBgReady(true)
    }

    image.src = nextBg

    return () => {
      cancelled = true
      image.onload = null
      image.onerror = null
    }
  }, [nextBg, bgImage])

  /* CONTEXT MENU */

  useEffect(() => {
    if (!trackMenuOpen) return

    const closeMenu = (event: PointerEvent) => {
      const target = event.target as HTMLElement

      if (
        target.closest("[data-track-context-menu]") ||
        target.closest("button")
      ) {
        return
      }

      setTrackMenuOpen(false)
    }

    document.addEventListener("pointerdown", closeMenu)

    return () => {
      document.removeEventListener("pointerdown", closeMenu)
    }
  }, [trackMenuOpen])

  useRenderTracker("PlayerMobil")

  if (isHidden) return null

  const headerTrackName = isRadio
    ? "Mixtape Radio"
    : currentSong?.songName || "Sin reproducción"

  const headerArtist = isRadio
    ? "En directo"
    : currentSong?.songArtist || ""

  return (
    <motion.aside
      variants={HOME_ANIMATION}
      initial="initial"
      animate="animate"
      exit="exit"
      className={`
        relative
        isolate
        flex
        h-full
        min-h-0
        w-full
        flex-col
        overflow-hidden
        bg-black
        select-none

        ${
          isLandscape
            ? "my-2 h-[calc(100%-1rem)] rounded-2xl border border-white/15"
            : ""
        }
      `}
      onPointerDown={(event) => {
        if (!trackMenuOpen) return

        const target = event.target as HTMLElement

        if (
          target.closest("[data-track-context-menu]") ||
          target.closest("button")
        ) {
          return
        }

        setTrackMenuOpen(false)
      }}
    >
      {/* BACKGROUND */}
      <PlayerBackground
        image={bgImage}
        ready={bgReady}
      />

      {/* SCROLL VIEWPORT */}
      <main
        className="
          relative
          z-10
          min-h-0
          flex-1
          w-full
          overflow-x-hidden
          overflow-y-auto
          overscroll-contain
          touch-pan-y
          scrollbar-none
        "
        style={{
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorX: "none",
          overscrollBehaviorY: "contain",
        }}
      >
        {isRadio ? (
          <div className="min-h-full w-full">
            <RadioPlayerSidebar />
          </div>
        ) : (
          <PlayerContent
            headerTrackName={headerTrackName}
            headerArtist={headerArtist}
            isPlaying={isPlaying}
            onClose={onClose}
            onMore={() => setTrackMenuOpen(true)}
            onHeaderClick={() => setTrackMenuOpen(false)}
            currentSong={currentSong}
            currentPlaylist={currentPlaylist}
            songsAcontinuacion={songsAcontinuacion}
            externalArtist={externalArtist}
          />
        )}
      </main>

      {/* CONTEXT MENU */}
      <div className="relative z-50">
        <TrackContextMenu
          open={trackMenuOpen}
          song={{
            songName:
              currentSong?.songName ||
              "Sin reproducción",

            songArtist:
              currentSong?.songArtist ||
              "",

            songimg:
              currentSong?.songimg ||
              currentTrackData?.trackImg,
          }}
          isFavorite={false}
          isOfflineSaved={false}
          isSavingOffline={false}
          onPlay={() => setTrackMenuOpen(false)}
          onArtist={() => setTrackMenuOpen(false)}
          onPlaylist={() => setTrackMenuOpen(false)}
          onFavorite={() => setTrackMenuOpen(false)}
          onOffline={() => setTrackMenuOpen(false)}
          onClose={() => setTrackMenuOpen(false)}
        />
      </div>
    </motion.aside>
  )
}

export default memo(PlayerMobil)